import type { Answer } from "../engine/engine.types.js";
import type { DeclaredBlocker, ResultReaction } from "../engine/recommend.js";

export type Screen =
  | "intro"
  | "consent"
  | "situations"
  | "differentiation"
  | "result"
  | "reaction"
  | "recommendation"
  | "save";

export const SESSION_VERSION = "1";

export interface SessionState {
  version: string;
  sessionId: string;
  screen: Screen;
  situationOrder: string[];
  differentiationOrder: string[];
  currentIndex: number;
  differentiationAsked: number;
  answers: Answer[];
  declaredBlocker: DeclaredBlocker;
  reaction: ResultReaction | null;
  consentGiven: boolean;
}

function pseudonymousId(): string {
  // Not derived from any personal data; regenerated per session, never sent
  // anywhere until (optionally, feature-flagged) an email is attached.
  return `iv-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export function createSession(situationOrder: string[]): SessionState {
  return {
    version: SESSION_VERSION,
    sessionId: pseudonymousId(),
    screen: "intro",
    situationOrder,
    differentiationOrder: [],
    currentIndex: 0,
    differentiationAsked: 0,
    answers: [],
    declaredBlocker: null,
    reaction: null,
    consentGiven: false,
  };
}

/** Full ordered sequence of question ids asked so far, situations then differentiation. */
function orderedQuestionIds(state: SessionState): string[] {
  return [...state.situationOrder, ...state.differentiationOrder];
}

/**
 * Records an answer. If the question already has an answer earlier in the
 * sequence (i.e. the user is going back and changing a previous answer),
 * every answer recorded *after* that point — including any differentiation
 * questions that were only asked because of it — is discarded, and
 * navigation resumes right after the changed question. This is the
 * invalidation rule from §3 pantalla 3 of the spec.
 */
export function answerQuestion(state: SessionState, answer: Answer): SessionState {
  const order = orderedQuestionIds(state);
  const position = order.indexOf(answer.questionId);
  if (position === -1) {
    throw new Error(`answerQuestion: unknown question id "${answer.questionId}"`);
  }

  const existingIndex = state.answers.findIndex((a) => a.questionId === answer.questionId);
  const isChange = existingIndex !== -1;

  let answers: Answer[];
  let differentiationOrder = state.differentiationOrder;
  let differentiationAsked = state.differentiationAsked;

  if (isChange) {
    // Keep only answers at or before this question's position, then replace it.
    answers = state.answers.filter((a) => order.indexOf(a.questionId) < position);
    answers.push(answer);
    // Any differentiation question scheduled after this position is invalidated too.
    const keptDifferentiation = state.differentiationOrder.filter(
      (id) => order.indexOf(id) <= position,
    );
    differentiationOrder = keptDifferentiation;
    differentiationAsked = keptDifferentiation.length;
  } else {
    answers = [...state.answers, answer];
  }

  const isSituation = state.situationOrder.includes(answer.questionId);
  const nextIndex = isSituation ? position + 1 : state.currentIndex;

  return {
    ...state,
    answers,
    differentiationOrder,
    differentiationAsked,
    currentIndex: nextIndex,
  };
}

export function scheduleDifferentiation(state: SessionState, questionId: string): SessionState {
  if (state.differentiationOrder.includes(questionId)) return state;
  const differentiationOrder = [...state.differentiationOrder, questionId];
  return {
    ...state,
    differentiationOrder,
    differentiationAsked: differentiationOrder.length,
    screen: "differentiation",
  };
}

export function goToScreen(state: SessionState, screen: Screen): SessionState {
  return { ...state, screen };
}

export function setDeclaredBlocker(state: SessionState, blocker: DeclaredBlocker): SessionState {
  return { ...state, declaredBlocker: blocker };
}

export function setReaction(state: SessionState, reaction: ResultReaction): SessionState {
  return { ...state, reaction };
}

export function giveConsent(state: SessionState): SessionState {
  return { ...state, consentGiven: true };
}

export function goBack(state: SessionState): SessionState {
  if (state.screen === "situations" && state.currentIndex > 0) {
    return { ...state, currentIndex: state.currentIndex - 1 };
  }
  const order: Screen[] = [
    "intro",
    "consent",
    "situations",
    "differentiation",
    "result",
    "reaction",
    "recommendation",
    "save",
  ];
  const idx = order.indexOf(state.screen);
  if (idx <= 0) return state;
  return { ...state, screen: order[idx - 1]! };
}
